import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Settings, Wand2, FileText, Loader2, Layers, Users, Building, Briefcase, RefreshCw, ChevronLeft, GitBranch, Workflow, Server } from 'lucide-react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { Skeleton } from '../../components/ui/Skeleton';
import { PillTabs, PillTabsList, PillTabsTrigger } from '../../components/ui/PillTabs';
import { usePermission } from '../../hooks/usePermission';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { apiClient } from '../../lib/api-client';
import { cn } from '../../lib/utils';
import { getRoleKey, roleKeysWithChildren } from '../../lib/ai-role-hierarchy';
import { type AiStreamSummary } from '../../components/admin/StreamConfigCard';
import { RoleConfigCard, type RoleSummary } from '../../components/admin/LeafConfigCard';
import { StreamConfigEditDialog } from '../../components/admin/StreamConfigEditDialog';
import { RoleConfigEditDialog } from '../../components/admin/RoleConfigEditDialog';
import type { StreamDefinitionLabelMaps } from '../../components/admin/RoleScopeChips';
import { OnboardStreamWizard } from '../../components/admin/OnboardStreamWizard';
import { ListenersDialog } from '../../components/admin/ListenersDialog';
import { AiScheduleDialog } from '../../components/admin/AiScheduleDialog';
import { BusinessFlowDialog } from '../../components/admin/BusinessFlowDialog';
import { AgentHierarchyView } from '../../components/admin/AgentHierarchyView';
import { ManageStreamsDialog } from '../../components/admin/ManageStreamsDialog';
import { ManageProductsDialog } from '../../components/admin/ManageProductsDialog';
import { ManageTeamsDialog } from '../../components/admin/ManageTeamsDialog';
import { ManageTenantsDialog } from '../../components/admin/ManageTenantsDialog';
import { OrgDraftChat } from '../../components/admin/OrgDraftChat';
import { RoleReconciliationDialog } from '../../components/admin/RoleReconciliationDialog';
import { CloudAgentEnvironmentsDialog } from '../../components/admin/CloudAgentEnvironmentsDialog';
import { RoleChatDrawer } from '../../components/admin/RoleChatDrawer';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog';
import { useNavigate, useParams } from 'react-router-dom';
import { setActiveAiWorkspaceId } from '../../lib/api-client';

type AiConfigApiFlatRole = {
  streamId: string;
  streamDisplayName: string;
  code: string;
  agentTitle?: string;
  agentDescription?: string;
  parentRef?: string;
  products?: string[];
  teams?: string[];
  tenants?: string[];
  enabled: boolean;
  roleEnabled?: boolean;
  streamEnabled?: boolean;
  effectiveEnabled?: boolean;
  adoProject?: string;
  adoAreaPath?: string;
  repo?: string;
  botDisplayName?: string;
  workflowTypeCount?: number;
  streamColor?: string;
  syncState?: {
    status: 'in_sync' | 'pending_sync' | 'syncing' | 'error';
    dirtyReasons?: string[];
    lastPrUrl?: string;
    lastError?: string;
    updatedAt?: string;
  };
};

type RoleSyncPreviewFile = {
  path: string;
  changed: boolean;
  content?: string;
  previousContent?: string;
};

type RoleSyncPreview = {
  repository?: string;
  sharedRepository?: {
    repository: string;
    roles: Array<{
      streamId: string;
      roleCode: string;
      roleTitle?: string;
    }>;
  };
  files?: RoleSyncPreviewFile[];
};

type SyncDialogState = {
  mode: 'single' | 'bulk';
  role?: RoleSummary;
  roles: RoleSummary[];
  preview?: RoleSyncPreview;
  loading: boolean;
  syncing: boolean;
  error?: string | null;
};

/** API `roles` entries omit legacy singular product/team/tenant fields — normalize for grid + hierarchy. */
function apiFlatRoleToRoleSummary(r: AiConfigApiFlatRole): RoleSummary {
  const prods = r.products ?? [];
  const tms = r.teams ?? [];
  const tns = r.tenants ?? [];
  return {
    streamId: r.streamId,
    streamDisplayName: r.streamDisplayName,
    code: r.code,
    products: prods,
    teams: tms,
    tenants: tns,
    product: prods[0] ?? '',
    productDisplayName: prods[0] ?? '',
    teamType: tms[0] ?? '',
    tenantCode: tns[0] ?? '',
    tenantDisplayName: tns[0] ?? '',
    agentTitle: r.agentTitle,
    agentDescription: r.agentDescription,
    parentRef: r.parentRef,
    enabled: r.enabled,
    roleEnabled: r.roleEnabled,
    streamEnabled: r.streamEnabled,
    effectiveEnabled: r.effectiveEnabled,
    adoProject: r.adoProject ?? '',
    adoAreaPath: r.adoAreaPath,
    repo: r.repo ?? '',
    botDisplayName: r.botDisplayName ?? '',
    workflowTypeCount: r.workflowTypeCount ?? 0,
    streamColor: r.streamColor,
    syncState: r.syncState,
  };
}

const SEED_DEFAULTS_YAML = `adoOrg: maxiron
jiraBaseUrl: https://maxiron.atlassian.net
botEndpointUrl: https://portal.maxiron.cloud/api/bot/messages
adoWebhookUrl: https://portal.maxiron.cloud/api/webhooks/ado-ai
defaultBranch: main
githubOrg: maxiron-cloud
githubTemplateRepo: maxiron-ai-agent-templates

teamTypes:
  - id: technical
    label: Technical
    reviewPath: github_pr_only
  - id: project
    label: Project
    reviewPath: sharepoint_and_github_pr
  - id: support
    label: Support
    reviewPath: sharepoint_and_github_pr

teamsChannelTypes:
  - id: default
    label: Default
  - id: escalation
    label: Escalation
`;

export function AiConfig() {
  const { workspaceId = 'maxiron' } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { isAllowed: canRead } = usePermission('ai_config', 'read');
  const { isAllowed: canManage } = usePermission('ai_config', 'manage');
  const { isAllowed: canChat } = usePermission('ai_config', 'chat');
  useEffect(() => {
    setActiveAiWorkspaceId(workspaceId);
  }, [workspaceId]);

  const [streams, setStreams] = useState<AiStreamSummary[]>([]);
  const [leaves, setLeaves] = useState<RoleSummary[]>([]);
  const [definitionLabelMaps, setDefinitionLabelMaps] = useState<StreamDefinitionLabelMaps | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const liveSyncProbeRef = useRef(0);
  const [filter, setFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'hierarchy' | 'draft'>('hierarchy');

  const [editOpen, setEditOpen] = useState(false);
  const [editStreamId, setEditStreamId] = useState('');
  const [editYaml, setEditYaml] = useState('');

  const [roleEditOpen, setRoleEditOpen] = useState(false);
  const [roleEditTarget, setRoleEditTarget] = useState<RoleSummary | null>(null);
  const [roleReconcileOpen, setRoleReconcileOpen] = useState(false);
  const [roleReconcileTarget, setRoleReconcileTarget] = useState<RoleSummary | null>(null);

  const [chatDrawerRole, setChatDrawerRole] = useState<RoleSummary | null>(null);
  const [chatDrawerInitialChatId, setChatDrawerInitialChatId] = useState<string | null>(null);
  const [roleActivityMap, setRoleActivityMap] = useState<
    Record<string, { working: boolean; activeChats: number; activeMissions: number }>
  >({});

  const [templateEditOpen, setTemplateEditOpen] = useState(false);
  const [templateEditStream, setTemplateEditStream] = useState('');
  const [templateYaml, setTemplateYaml] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsYaml, setDefaultsYaml] = useState('');
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [defaultsError, setDefaultsError] = useState<string | null>(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [listenersOpen, setListenersOpen] = useState(false);
  const [listenersStreamId, setListenersStreamId] = useState('');
  const [listenersStreamName, setListenersStreamName] = useState('');
  const [listenersRoleCode, setListenersRoleCode] = useState<string | undefined>(undefined);
  const [listenersRoleLabel, setListenersRoleLabel] = useState<string | undefined>(undefined);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [schedulesStreamId, setSchedulesStreamId] = useState('');
  const [schedulesStreamName, setSchedulesStreamName] = useState('');
  const [schedulesRoleCode, setSchedulesRoleCode] = useState<string | undefined>(undefined);
  const [schedulesRoleLabel, setSchedulesRoleLabel] = useState<string | undefined>(undefined);
  const [manageStreamsOpen, setManageStreamsOpen] = useState(false);
  const [manageProductsOpen, setManageProductsOpen] = useState(false);
  const [manageTeamsOpen, setManageTeamsOpen] = useState(false);
  const [manageTenantsOpen, setManageTenantsOpen] = useState(false);
  const [managingStreamId, setManagingStreamId] = useState('');
  const [businessFlowOpen, setBusinessFlowOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<RoleSummary | null>(null);
  const [retryingStream, setRetryingStream] = useState<string | null>(null);
  const [retryResult, setRetryResult] = useState<{ streamId: string; steps: any[]; overallStatus: string } | null>(null);

  interface LeafResourceItem {
    type: string;
    name: string;
    shared: boolean;
    metadata?: {
      appName?: string;
      teamName?: string;
      chatName?: string;
      channelId?: string;
      channelKind?: 'default' | 'escalation';
    };
  }
  const [deleteResources, setDeleteResources] = useState<LeafResourceItem[] | null>(null);
  const [deleteChecked, setDeleteChecked] = useState<Set<string>>(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);
  const [deleteLiveSteps, setDeleteLiveSteps] = useState<any[]>([]);
  const [deleteResult, setDeleteResult] = useState<any>(null);
  const [syncingRoleKeys, setSyncingRoleKeys] = useState<Set<string>>(new Set());
  const [syncDialog, setSyncDialog] = useState<SyncDialogState | null>(null);
  const [cloudEnvironmentsOpen, setCloudEnvironmentsOpen] = useState(false);
  const [trainerSyncDialog, setTrainerSyncDialog] = useState<{
    loading: boolean;
    syncing: boolean;
    error: string | null;
    repository?: string;
    files?: Array<{ path: string; changed: boolean }>;
    result?: { prUrl?: string; createdRepository?: boolean };
  } | null>(null);

  const getDeleteResourceKey = (resource: LeafResourceItem) =>
    [
      resource.type,
      resource.metadata?.channelId,
      resource.metadata?.teamName,
      resource.metadata?.chatName,
      resource.metadata?.appName,
      resource.name,
    ]
      .filter(Boolean)
      .join(':');

  useActivityHeartbeat('cache:ai-stream-configs', canRead);

  const loadStreams = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get<{
        streams: AiStreamSummary[];
        leaves: RoleSummary[];
        roles?: AiConfigApiFlatRole[];
        definitionLabelMaps?: StreamDefinitionLabelMaps;
      }>('/api/admin/ai-config/streams');
      setStreams(res.streams ?? []);
      setDefinitionLabelMaps(res.definitionLabelMaps);
      const merged = new Map<string, RoleSummary>();
      for (const l of res.leaves ?? []) merged.set(getRoleKey(l), l);
      for (const r of res.roles ?? []) {
        const row = apiFlatRoleToRoleSummary(r);
        const k = getRoleKey(row);
        const existing = merged.get(k);
        if (!existing) {
          merged.set(k, row);
        } else {
          merged.set(k, { ...existing, syncState: row.syncState || existing.syncState });
        }
      }
      setLeaves([...merged.values()]);
      setLoadVersion((value) => value + 1);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load streams');
      setStreams([]);
      setLeaves([]);
      setDefinitionLabelMaps(undefined);
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  const loadRoleActivity = useCallback(async () => {
    if (!canChat || streams.length === 0) {
      setRoleActivityMap({});
      return;
    }
    const streamIdsForActivity = [...new Set(streams.map((stream) => stream.streamId).filter(Boolean))];
    const activityMap: Record<string, { working: boolean; activeChats: number; activeMissions: number }> = {};
    await Promise.all(streamIdsForActivity.map(async (streamId) => {
      try {
        const res = await apiClient.getAiActiveAgents(streamId);
        const roleRows = res.roles || {};
        for (const [roleCode, data] of Object.entries(roleRows)) {
          const matching = leaves.filter((leaf) => leaf.streamId === streamId && leaf.code === roleCode);
          for (const leaf of matching) {
            activityMap[getRoleKey(leaf)] = data;
          }
        }
      } catch {
        // ignore activity errors per stream
      }
    }));
    setRoleActivityMap(activityMap);
  }, [canChat, leaves, streams]);

  useEffect(() => {
    void loadStreams();
  }, [loadStreams]);

  useEffect(() => {
    if (!canRead || loadVersion === 0 || leaves.length === 0) return;
    const probeId = liveSyncProbeRef.current + 1;
    liveSyncProbeRef.current = probeId;
    let cancelled = false;

    const probeRole = async (role: RoleSummary) => {
      const roleCode = String(role.code || '').trim();
      if (!role.streamId || !roleCode) return;
      try {
        const preview = await apiClient.previewAiRoleSync(role.streamId, roleCode);
        if (cancelled || liveSyncProbeRef.current !== probeId) return;
        const changedFiles = Array.isArray(preview?.files)
          ? preview.files.filter((file: any) => file?.changed).length
          : 0;
        setLeaves((current) => current.map((item) => {
          if (getRoleKey(item) !== getRoleKey(role)) return item;
          return {
            ...item,
            syncState: {
              ...(item.syncState || {}),
              status: changedFiles > 0 ? 'pending_sync' : 'in_sync',
              dirtyReasons: changedFiles > 0 ? ['artifact_files_changed'] : [],
              lastError: undefined,
              updatedAt: new Date().toISOString(),
            },
          };
        }));
      } catch (err: unknown) {
        if (cancelled || liveSyncProbeRef.current !== probeId) return;
        setLeaves((current) => current.map((item) => {
          if (getRoleKey(item) !== getRoleKey(role)) return item;
          return {
            ...item,
            syncState: {
              ...(item.syncState || {}),
              status: item.syncState?.status || 'pending_sync',
              lastError: err instanceof Error ? err.message : 'Live sync check failed',
              updatedAt: new Date().toISOString(),
            },
          };
        }));
      }
    };

    void (async () => {
      const roles = [...leaves];
      const concurrency = 3;
      for (let index = 0; index < roles.length; index += concurrency) {
        if (cancelled || liveSyncProbeRef.current !== probeId) return;
        await Promise.all(roles.slice(index, index + concurrency).map(probeRole));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canRead, leaves.length, loadVersion]);

  useEffect(() => {
    void loadRoleActivity();
    if (!canChat) return;
    const timer = window.setInterval(() => {
      void loadRoleActivity();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [canChat, loadRoleActivity]);

  useCacheEvents({
    enabled: canRead,
    cacheNames: ['ai-stream-configs'],
    onEvent: () => {
      void loadStreams();
    },
  });

  const streamIds = [...new Set(streams.map((s) => s.streamId).filter(Boolean))].sort();
  const filteredLeaves = filter === 'all' ? leaves : leaves.filter((l) => l.streamId === filter);
  const pendingSyncCount = filteredLeaves.filter((leaf) => {
    const status = leaf.syncState?.status || 'pending_sync';
    return status === 'pending_sync' || status === 'error';
  }).length;
  const roleKeysThatHaveChildRoles = useMemo(() => roleKeysWithChildren(leaves), [leaves]);
  const streamPickerOptions = useMemo(
    () =>
      [...streams]
        .sort((a, b) => a.streamId.localeCompare(b.streamId))
        .map((s) => ({ streamId: s.streamId, displayName: s.displayName })),
    [streams],
  );
  const defaultEntityDialogStreamId = filter !== 'all' ? filter : streamIds[0] ?? '';

  const handleEditRole = (leaf: RoleSummary) => {
    if (!leaf.code?.trim()) {
      void openEdit({ streamId: leaf.streamId, displayName: leaf.streamDisplayName } as AiStreamSummary);
      return;
    }
    setRoleEditTarget(leaf);
    setRoleEditOpen(true);
  };

  const handleReconcileRole = (leaf: RoleSummary) => {
    if (!leaf.code?.trim()) return;
    setRoleReconcileTarget(leaf);
    // Open on next frame so the dropdown click that triggered this action
    // cannot immediately bubble into dialog backdrop close handling.
    requestAnimationFrame(() => setRoleReconcileOpen(true));
  };

  const handleChatRole = (leaf: RoleSummary) => {
    if (!leaf.code?.trim()) return;
    setChatDrawerInitialChatId(null);
    setChatDrawerRole(leaf);
  };

  const handleOpenRoleActivity = (leaf: RoleSummary) => {
    if (!leaf.code?.trim()) return;
    setChatDrawerInitialChatId(null);
    setChatDrawerRole(leaf);
  };

  const handleOpenRoleChat = (roleCode: string, chatId: string) => {
    const leaf = leaves.find((item) => item.streamId === chatDrawerRole?.streamId && item.code === roleCode)
      || leaves.find((item) => item.code === roleCode);
    if (!leaf) return;
    setChatDrawerInitialChatId(chatId);
    setChatDrawerRole(leaf);
  };

  const openEdit = async (stream: AiStreamSummary) => {
    setEditStreamId(stream.streamId);
    setEditOpen(false);
    setEditYaml('');
    try {
      const res = await apiClient.get<{ yaml: string }>(
        `/api/admin/ai-config/streams/${encodeURIComponent(stream.streamId)}`,
      );
      setEditYaml(res.yaml);
      setEditOpen(true);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load stream YAML');
    }
  };

  const handleOpenListeners = (leaf: RoleSummary) => {
    setListenersStreamId(leaf.streamId);
    setListenersStreamName(leaf.streamDisplayName);
    setListenersRoleCode(leaf.code?.trim() || undefined);
    setListenersRoleLabel(leaf.agentTitle || leaf.tenantDisplayName);
    setListenersOpen(true);
  };

  const handleOpenSchedules = (leaf: RoleSummary) => {
    setSchedulesStreamId(leaf.streamId);
    setSchedulesStreamName(leaf.streamDisplayName);
    setSchedulesRoleCode(leaf.code?.trim() || undefined);
    setSchedulesRoleLabel(leaf.agentTitle || leaf.tenantDisplayName);
    setSchedulesOpen(true);
  };

  const handleSetParentLink = async (leaf: RoleSummary, parentRef?: string) => {
    const encodedStreamId = encodeURIComponent(leaf.streamId);
    const current = await apiClient.get<{ yaml: string }>(`/api/admin/ai-config/streams/${encodedStreamId}`);
    const parsed = yaml.load(current.yaml) as any;
    const root = parsed?.stream || parsed || {};

    if (leaf.code) {
      const roles = Array.isArray(root.roles) ? root.roles : [];
      const idx = roles.findIndex((r: any) => String(r?.code || '') === leaf.code);
      if (idx >= 0) {
        if (parentRef) roles[idx].parentRef = parentRef;
        else delete roles[idx].parentRef;
      }
    } else {
      const products = Array.isArray(root.products) ? root.products : [];
      for (const product of products) {
        if (product?.id !== leaf.product) continue;
        const teamTypes = Array.isArray(product.teamTypes) ? product.teamTypes : [];
        for (const teamType of teamTypes) {
          if (teamType?.id !== leaf.teamType) continue;
          const tenants = Array.isArray(teamType.tenants) ? teamType.tenants : [];
          for (const tenant of tenants) {
            if (tenant?.code !== leaf.tenantCode) continue;
            if (parentRef) tenant.parentRef = parentRef;
            else delete tenant.parentRef;
          }
        }
      }
    }

    const nextYaml = yaml.dump(parsed, { lineWidth: 120 });
    await apiClient.put(`/api/admin/ai-config/streams/${encodedStreamId}`, { yaml: nextYaml });
    await loadStreams();
  };

  const openTemplateEdit = async (stream: AiStreamSummary) => {
    setTemplateEditStream(stream.streamId);
    setTemplateError(null);
    setTemplateYaml('');
    try {
      const res = await apiClient.get<{ yaml: string }>(
        `/api/admin/ai-config/templates/${encodeURIComponent(stream.streamId)}`,
      );
      setTemplateYaml(res.yaml || '');
      setTemplateEditOpen(true);
    } catch {
      setTemplateYaml('');
      setTemplateEditOpen(true);
    }
  };

  const handleTemplateSave = async () => {
    setTemplateError(null);
    try { yaml.load(templateYaml); } catch (e: any) {
      setTemplateError(e.message);
      return;
    }
    setTemplateSaving(true);
    try {
      await apiClient.put(`/api/admin/ai-config/templates/${encodeURIComponent(templateEditStream)}`, { yaml: templateYaml });
      setTemplateEditOpen(false);
    } catch (err: unknown) {
      setTemplateError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setTemplateSaving(false);
    }
  };

  const openDefaults = async () => {
    setDefaultsError(null);
    setDefaultsYaml('');
    try {
      const res = await apiClient.get<{ yaml: string }>('/api/admin/ai-config/defaults');
      setDefaultsYaml(res.yaml || SEED_DEFAULTS_YAML);
      setDefaultsOpen(true);
    } catch {
      setDefaultsYaml(SEED_DEFAULTS_YAML);
      setDefaultsOpen(true);
    }
  };

  const handleDefaultsSave = async () => {
    setDefaultsError(null);
    try { yaml.load(defaultsYaml); } catch (e: any) {
      setDefaultsError(e.message);
      return;
    }
    setDefaultsSaving(true);
    try {
      await apiClient.put('/api/admin/ai-config/defaults', { yaml: defaultsYaml });
      setDefaultsOpen(false);
    } catch (err: unknown) {
      setDefaultsError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setDefaultsSaving(false);
    }
  };

  const handleRoleEnabledToggle = async (role: RoleSummary) => {
    const roleCode = String(role.code || '').trim();
    if (!roleCode) return;
    try {
      const roleEnabled = role.roleEnabled ?? role.enabled;
      await apiClient.put(
        `/api/admin/ai-config/streams/${encodeURIComponent(role.streamId)}/roles/${encodeURIComponent(roleCode)}`,
        { enabled: !roleEnabled },
      );
      await loadStreams();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Request failed');
    }
  };

  const handleSyncRole = async (role: RoleSummary) => {
    const roleCode = String(role.code || '').trim();
    if (!roleCode) return;
    setSyncDialog({
      mode: 'single',
      role,
      roles: [role],
      loading: true,
      syncing: false,
      error: null,
    });
    try {
      const preview = await apiClient.previewAiRoleSync(role.streamId, roleCode);
      const sharedRoles = (preview?.sharedRepository?.roles || [])
        .map((sharedRole: { streamId: string; roleCode: string; roleTitle?: string }) => {
          const existing = leaves.find((leaf) => leaf.streamId === sharedRole.streamId && leaf.code === sharedRole.roleCode);
          return existing || {
            streamId: sharedRole.streamId,
            streamDisplayName: sharedRole.streamId,
            code: sharedRole.roleCode,
            agentTitle: sharedRole.roleTitle || sharedRole.roleCode,
            repo: preview.sharedRepository?.repository || preview.repository || role.repo,
            enabled: true,
          } as RoleSummary;
        });
      setSyncDialog({
        mode: 'single',
        role,
        roles: sharedRoles.length > 0 ? sharedRoles : [role],
        preview,
        loading: false,
        syncing: false,
        error: null,
      });
    } catch (err: unknown) {
      setSyncDialog((prev) => prev ? {
        ...prev,
        loading: false,
        syncing: false,
        error: err instanceof Error ? err.message : 'Role sync preview failed',
      } : null);
    }
  };

  const confirmSyncDialog = async () => {
    if (!syncDialog || syncDialog.syncing) return;
    const targets = syncDialog.roles.filter((role) => String(role.code || '').trim());
    if (targets.length === 0) {
      setSyncDialog(null);
      return;
    }
    setSyncingRoleKeys(new Set(targets.map((role) => getRoleKey(role))));
    setSyncDialog((prev) => prev ? { ...prev, syncing: true, error: null } : null);
    try {
      if (syncDialog.mode === 'single' && syncDialog.role) {
        const roleCode = String(syncDialog.role.code || '').trim();
        await apiClient.syncAiRole(syncDialog.role.streamId, roleCode);
      } else {
        const roleCodesByStream = new Map<string, string[]>();
        for (const role of targets) {
          const roleCode = String(role.code || '').trim();
          if (!roleCode) continue;
          const rows = roleCodesByStream.get(role.streamId) || [];
          rows.push(roleCode);
          roleCodesByStream.set(role.streamId, rows);
        }
        for (const [streamId, roleCodes] of roleCodesByStream.entries()) {
          await apiClient.syncAiStream(streamId, roleCodes);
        }
      }
      setSyncDialog(null);
      await loadStreams();
    } catch (err: unknown) {
      setSyncDialog((prev) => prev ? {
        ...prev,
        syncing: false,
        error: err instanceof Error ? err.message : 'Sync failed',
      } : null);
    } finally {
      setSyncingRoleKeys(new Set());
    }
  };

  const openBulkSyncPreview = (targets: RoleSummary[]) => {
    if (targets.length === 0) return;
    setSyncDialog({
      mode: 'bulk',
      roles: targets,
      loading: false,
      syncing: false,
      error: null,
    });
  };

  const syncDialogChangedFiles = (syncDialog?.preview?.files || []).filter((file) => file.changed);
  const syncDialogUnchangedFiles = (syncDialog?.preview?.files || []).filter((file) => !file.changed);
  const syncDialogSharedRoles = syncDialog?.preview?.sharedRepository?.roles || [];
  const syncDialogBulkByStream = useMemo(() => {
    const groups = new Map<string, RoleSummary[]>();
    for (const role of syncDialog?.mode === 'bulk' ? syncDialog.roles : []) {
      const rows = groups.get(role.streamId) || [];
      rows.push(role);
      groups.set(role.streamId, rows);
    }
    return [...groups.entries()];
  }, [syncDialog]);

  const closeSyncDialog = () => {
    if (syncDialog?.syncing) return;
    setSyncDialog(null);
  };

  const handleSyncPendingFiltered = async () => {
    const targets = filteredLeaves.filter((leaf) => {
      const status = leaf.syncState?.status || 'pending_sync';
      return status === 'pending_sync' || status === 'error';
    });
    openBulkSyncPreview(targets);
  };

  const openLeafDeleteDialog = async (leaf: RoleSummary) => {
    setDeleteTarget(leaf);
    setDeleteResources(null);
    setDeleteChecked(new Set());
    setDeleteErrors([]);
    setDeleteLiveSteps([]);
    setDeleteResult(null);
    try {
      const res = await apiClient.get<{ resources: LeafResourceItem[] }>(
        `/api/admin/ai-config/leaves/${encodeURIComponent(leaf.streamId)}/${encodeURIComponent(leaf.product)}/${encodeURIComponent(leaf.teamType)}/${encodeURIComponent(leaf.tenantCode)}/resources`,
      );
      setDeleteResources(res.resources);
      const nonShared = res.resources.filter((r) => !r.shared).map((r) => getDeleteResourceKey(r));
      setDeleteChecked(new Set(nonShared));
    } catch {
      setDeleteResources([]);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteErrors([]);
    setDeleteLiveSteps([]);
    setDeleteResult(null);

    const checkedResources = deleteResources?.filter((r) => deleteChecked.has(getDeleteResourceKey(r))) || [];
    const body = {
      deleteRepos: checkedResources.filter((r) => r.type === 'repo').map((r) => r.name),
      deleteAdoProjects: checkedResources.filter((r) => r.type === 'adoProject').map((r) => r.name),
      deleteBots: checkedResources.filter((r) => r.type === 'bot').map((r) => r.name),
      deleteBotServices: checkedResources.filter((r) => r.type === 'botService').map((r) => r.name),
      deleteTeamsApps: checkedResources.filter((r) => r.type === 'teamsApp').map((r) => r.name),
      deleteTeamsTeams: checkedResources.filter((r) => r.type === 'teamsTeam').map((r) => r.name),
      deleteTeamsChats: checkedResources.filter((r) => r.type === 'teamsChat').map((r) => r.name),
      deleteTeamsChannels: checkedResources
        .filter((r) => r.type === 'teamsChannel' && r.metadata?.teamName && r.metadata?.channelId)
        .map((r) => ({
          teamName: r.metadata!.teamName!,
          channelId: r.metadata!.channelId!,
          channelKind: r.metadata?.channelKind,
        })),
      deleteTeamsTeamInstalls: checkedResources
        .filter((r) => r.type === 'teamsTeamInstall' && r.metadata?.teamName && r.metadata?.appName)
        .map((r) => ({
          teamName: r.metadata!.teamName!,
          appName: r.metadata!.appName!,
        })),
      deleteTeamsChatInstalls: checkedResources
        .filter((r) => r.type === 'teamsChatInstall' && r.metadata?.chatName && r.metadata?.appName)
        .map((r) => ({
          chatName: r.metadata!.chatName!,
          appName: r.metadata!.appName!,
        })),
      deleteTeamsUserInstalls: checkedResources
        .filter((r) => r.type === 'teamsUserInstall' && r.metadata?.teamName && r.metadata?.appName)
        .map((r) => ({
          teamName: r.metadata!.teamName!,
          appName: r.metadata!.appName!,
        })),
    };

    const url = `/api/admin/ai-config/leaves/${encodeURIComponent(deleteTarget.streamId)}/${encodeURIComponent(deleteTarget.product)}/${encodeURIComponent(deleteTarget.teamType)}/${encodeURIComponent(deleteTarget.tenantCode)}/delete`;

    try {
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'x-workspace-id': workspaceId,
      };
      const accessToken = localStorage.getItem('oci_access_token');
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

      const response = await fetch(`${apiBase}${url}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errText = await response.text();
        setDeleteErrors([errText || `HTTP ${response.status}`]);
        setDeleteLoading(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        setDeleteErrors(['Streaming not supported']);
        setDeleteLoading(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (currentEvent === 'step') {
                setDeleteLiveSteps((prev) => {
                  const idx = prev.findIndex((s) => s.id === parsed.id);
                  if (idx >= 0) {
                    const updated = [...prev];
                    updated[idx] = parsed;
                    return updated;
                  }
                  return [...prev, parsed];
                });
              } else if (currentEvent === 'complete') {
                setDeleteResult(parsed);
                if (parsed.errors?.length) setDeleteErrors(parsed.errors);
                await loadStreams();
              } else if (currentEvent === 'error') {
                setDeleteErrors([parsed.error || 'Delete failed']);
              }
            } catch { /* ignore parse errors */ }
            currentEvent = '';
          }
        }
      }
    } catch (err: unknown) {
      setDeleteErrors([err instanceof Error ? err.message : 'Delete failed']);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRetryOnboarding = async (streamId: string) => {
    if (retryingStream) return;
    setRetryingStream(streamId);
    setRetryResult(null);
    try {
      const result = await apiClient.post<{ streamId: string; steps: any[]; overallStatus: string }>(
        `/api/admin/ai-onboarding/stream/retry/${encodeURIComponent(streamId)}`,
      );
      setRetryResult(result);
      await loadStreams();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetryingStream(null);
    }
  };

  const closeTrainerSyncDialog = () => {
    if (trainerSyncDialog?.loading || trainerSyncDialog?.syncing) return;
    setTrainerSyncDialog(null);
  };

  const openTrainerSyncPreview = async () => {
    setTrainerSyncDialog({ loading: true, syncing: false, error: null });
    try {
      const preview = await apiClient.previewOrgSetupSync();
      setTrainerSyncDialog({
        loading: false,
        syncing: false,
        error: null,
        repository: preview?.repository,
        files: Array.isArray(preview?.files) ? preview.files : [],
      });
    } catch (err: unknown) {
      setTrainerSyncDialog({
        loading: false,
        syncing: false,
        error: err instanceof Error ? err.message : 'AI Training sync preview failed',
      });
    }
  };

  const confirmTrainerSync = async () => {
    if (!trainerSyncDialog || trainerSyncDialog.loading || trainerSyncDialog.syncing) return;
    setTrainerSyncDialog((prev) => prev ? { ...prev, syncing: true, error: null } : prev);
    try {
      const result = await apiClient.syncOrgSetupTrainer();
      setTrainerSyncDialog((prev) => prev ? {
        ...prev,
        syncing: false,
        repository: result?.repository || prev.repository,
        files: Array.isArray(result?.files) ? result.files : prev.files,
        result: {
          prUrl: result?.prUrl,
          createdRepository: Boolean(result?.createdRepository),
        },
      } : prev);
    } catch (err: unknown) {
      setTrainerSyncDialog((prev) => prev ? {
        ...prev,
        syncing: false,
        error: err instanceof Error ? err.message : 'AI Training sync failed',
      } : prev);
    }
  };

  const actionItems = [
    streamIds.length > 0
      ? {
          label: 'Business Flow',
          icon: <Workflow className="w-4 h-4" />,
          onClick: () => setBusinessFlowOpen(true),
        }
      : null,
    canManage
      ? {
          label: 'Manage Streams',
          icon: <Layers className="w-4 h-4" />,
          onClick: () => setManageStreamsOpen(true),
        }
      : null,
    canManage && streamIds.length > 0
      ? {
          label: 'Manage Products',
          icon: <Briefcase className="w-4 h-4" />,
          onClick: () => {
            setManagingStreamId(defaultEntityDialogStreamId);
            setManageProductsOpen(true);
          },
        }
      : null,
    canManage && streamIds.length > 0
      ? {
          label: 'Manage Teams',
          icon: <Users className="w-4 h-4" />,
          onClick: () => {
            setManagingStreamId(defaultEntityDialogStreamId);
            setManageTeamsOpen(true);
          },
        }
      : null,
    canManage && streamIds.length > 0
      ? {
          label: 'Manage Tenants',
          icon: <Building className="w-4 h-4" />,
          onClick: () => {
            setManagingStreamId(defaultEntityDialogStreamId);
            setManageTenantsOpen(true);
          },
        }
      : null,
    canManage && streamIds.length > 0
      ? {
          label: 'Reconcile Resources',
          icon: <RefreshCw className="w-4 h-4" />,
          onClick: () => setManageStreamsOpen(true),
        }
      : null,
    canManage && pendingSyncCount > 0
      ? {
          label: `Sync Pending Roles (${pendingSyncCount})`,
          icon: <RefreshCw className="w-4 h-4" />,
          onClick: () => void handleSyncPendingFiltered(),
        }
      : null,
    canManage
      ? {
          label: 'Cloud agent environments',
          icon: <Server className="w-4 h-4" />,
          onClick: () => setCloudEnvironmentsOpen(true),
        }
      : null,
    canManage
      ? {
          label: 'Sync AI Training',
          icon: <GitBranch className="w-4 h-4" />,
          onClick: () => void openTrainerSyncPreview(),
        }
      : null,
    canManage
      ? {
          label: 'Onboard Role',
          icon: <Wand2 className="w-4 h-4" />,
          onClick: () => setWizardOpen(true),
        }
      : null,
    canManage
      ? {
          label: 'Edit Defaults',
          icon: <FileText className="w-4 h-4" />,
          onClick: () => void openDefaults(),
        }
      : null,
  ].filter(Boolean) as any[];

  if (!canRead) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700 dark:text-gray-300">You do not have permission to view AI workspaces.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full max-w-full flex-1 flex-col gap-4 pb-6">
      <PageHeader
        icon={Settings}
        iconColor="text-violet-600 dark:text-violet-400"
        iconBgColor="bg-violet-100 dark:bg-violet-900/30"
        description={`AI Workspaces (${workspaceId}) — Manage organizational streams, roles, and AI agent configurations.`}
        count={filteredLeaves.length}
        countLabel="role configurations"
        countColor="text-violet-600 dark:text-violet-400"
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-stretch gap-2 w-full sm:w-auto h-full">
            <Button variant="ghost" className="h-full w-full sm:w-auto gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" onClick={() => navigate('/admin/ai-config')}>
              <ChevronLeft className="w-4 h-4" />
              Workspaces
            </Button>
            {actionItems.length > 0 ? (
              <DropdownMenu
                className="h-full w-full sm:w-auto"
                trigger={
                  <Button variant="outline" className="h-full w-full gap-2">
                    <Settings className="w-4 h-4" />
                    Actions
                  </Button>
                }
                items={actionItems}
              />
            ) : null}
          </div>
        }
      />

      <PillTabs value={viewMode} onValueChange={(v) => setViewMode(v as 'grid' | 'hierarchy' | 'draft')}>
        <PillTabsList>
          <PillTabsTrigger value="hierarchy">Hierarchy</PillTabsTrigger>
          <PillTabsTrigger value="grid">Grid</PillTabsTrigger>
          {canManage && <PillTabsTrigger value="draft">AI Training</PillTabsTrigger>}
        </PillTabsList>
      </PillTabs>

      {viewMode !== 'draft' && streamIds.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Stream</span>
          <div className="flex flex-wrap gap-1.5">
            {['all', ...streamIds].map((id) => {
              const label = id === 'all'
                ? 'All'
                : streams.find((st) => st.streamId === id)?.displayName || id.charAt(0).toUpperCase() + id.slice(1);
              const streamAccent =
                id !== 'all' ? streams.find((st) => st.streamId === id)?.color?.trim() : '';
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                    streamAccent ? 'pl-2 border-l-[3px] border-solid border-transparent' : '',
                    filter === id
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                  )}
                  style={
                    streamAccent
                      ? { borderLeftColor: streamAccent }
                      : undefined
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loadError && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {viewMode === 'draft' ? (
          <OrgDraftChat
            className="min-h-0 min-w-0 flex-1 overflow-hidden"
            existingStreams={streams.map((s) => ({ streamId: s.streamId, displayName: s.displayName, enabled: s.enabled }))}
            onApplied={() => void loadStreams()}
            onStreamApplyNavigate={() => {
              void loadStreams();
              setViewMode('hierarchy');
            }}
          />
        ) : loading ? (
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48 w-full rounded-lg" />
            ))}
          </div>
        ) : filteredLeaves.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-gray-600 dark:text-gray-400">
              No streams configured. Use Actions &gt; Onboard Role to get started.
            </CardContent>
          </Card>
        ) : viewMode === 'hierarchy' ? (
          <AgentHierarchyView
            className="min-h-0 flex-1"
            roles={filteredLeaves}
            allRoles={leaves}
            definitionLabelMaps={definitionLabelMaps}
            canManage={canManage}
            canChat={canChat}
            onChatRole={handleChatRole}
            onEditRole={handleEditRole}
            onPauseToggle={handleRoleEnabledToggle}
            onDelete={(l) => void openLeafDeleteDialog(l)}
            onListeners={handleOpenListeners}
            onSchedules={handleOpenSchedules}
            onReconcile={handleReconcileRole}
            onSyncRole={(leaf) => void handleSyncRole(leaf)}
            onSetParent={handleSetParentLink}
            roleActivity={roleActivityMap}
            syncingRoleKeys={syncingRoleKeys}
            onOpenActiveChats={handleOpenRoleActivity}
          />
        ) : (
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto pb-1 md:grid-cols-2 xl:grid-cols-3">
            {filteredLeaves.map((leaf) => (
              <RoleConfigCard
                key={getRoleKey(leaf)}
                role={leaf}
                applyStreamLeafColor={!roleKeysThatHaveChildRoles.has(getRoleKey(leaf))}
                definitionLabelMaps={definitionLabelMaps}
                canManage={canManage}
                canChat={canChat}
                onEditRole={handleEditRole}
                onPauseToggle={handleRoleEnabledToggle}
                onDelete={(l) => void openLeafDeleteDialog(l)}
                onListeners={handleOpenListeners}
                onSchedules={handleOpenSchedules}
                onReconcile={handleReconcileRole}
                onSyncRole={(leaf) => void handleSyncRole(leaf)}
                syncing={syncingRoleKeys.has(getRoleKey(leaf))}
                onChatRole={handleChatRole}
              />
            ))}
          </div>
        )}
      </div>

      <StreamConfigEditDialog
        open={editOpen}
        streamId={editStreamId}
        initialYaml={editYaml}
        onClose={() => setEditOpen(false)}
        onSaved={() => void loadStreams()}
      />

      <RoleConfigEditDialog
        open={roleEditOpen}
        role={roleEditTarget}
        onClose={() => {
          setRoleEditOpen(false);
          setRoleEditTarget(null);
        }}
        onSaved={() => void loadStreams()}
      />

      <CloudAgentEnvironmentsDialog
        open={cloudEnvironmentsOpen}
        onClose={() => setCloudEnvironmentsOpen(false)}
      />

      {roleReconcileTarget?.code && (
        <RoleReconciliationDialog
          open={roleReconcileOpen}
          streamId={roleReconcileTarget.streamId}
          roleCode={roleReconcileTarget.code}
          roleLabel={roleReconcileTarget.agentTitle || roleReconcileTarget.code}
          onClose={() => {
            setRoleReconcileOpen(false);
            setRoleReconcileTarget(null);
          }}
          onChanged={() => void loadStreams()}
        />
      )}

      <RoleChatDrawer
        open={!!chatDrawerRole}
        onClose={() => {
          setChatDrawerRole(null);
          setChatDrawerInitialChatId(null);
        }}
        streamId={chatDrawerRole?.streamId || ''}
        role={chatDrawerRole}
        initialChatId={chatDrawerInitialChatId}
        onOpenRoleChat={handleOpenRoleChat}
      />

      <Dialog open={!!syncDialog} onOpenChange={(open) => !open && closeSyncDialog()}>
        <DialogContent maxWidth="5xl" className="max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {syncDialog?.mode === 'bulk' ? 'Sync Pending Role Artifacts' : 'Sync Role Artifacts'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4 overflow-y-auto">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100">
              Review what is outstanding before syncing. Proceeding creates generated Cursor artifact changes in the role repository via the configured sync branch and PR flow. Cancel leaves the role sync state unchanged.
            </div>

            {syncDialog?.error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                {syncDialog.error}
              </div>
            ) : null}

            {syncDialog?.loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating artifact preview...
              </div>
            ) : syncDialog?.mode === 'single' ? (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Role</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{syncDialog.role?.agentTitle || syncDialog.role?.code}</p>
                    <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{syncDialog.role?.streamId}/{syncDialog.role?.code}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Repository</p>
                    <p className="break-all text-sm text-gray-900 dark:text-gray-100">{syncDialog.preview?.repository || syncDialog.role?.repo || 'Not configured'}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Outstanding</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{syncDialogChangedFiles.length} changed files</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{syncDialogUnchangedFiles.length} unchanged files</p>
                  </div>
                </div>

                {syncDialogSharedRoles.length > 1 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge variant="warning">Shared repository</Badge>
                      <span>
                        Proceeding syncs the deterministic shared repo files once and refreshes sync state for all roles using this repository.
                      </span>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      {syncDialogSharedRoles.map((sharedRole) => (
                        <div key={`${sharedRole.streamId}/${sharedRole.roleCode}`} className="rounded border border-amber-200/70 bg-white/70 p-2 dark:border-amber-900/50 dark:bg-gray-900/40">
                          <p className="font-medium">{sharedRole.roleTitle || sharedRole.roleCode}</p>
                          <p className="font-mono text-xs opacity-80">{sharedRole.streamId}/{sharedRole.roleCode}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Files that will be synced</h4>
                  {syncDialogChangedFiles.length === 0 ? (
                    <p className="rounded-lg border border-gray-200 p-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      No file differences were detected. You can cancel, or proceed to refresh sync state.
                    </p>
                  ) : (
                    <div className="max-h-80 space-y-2 overflow-y-auto">
                      {syncDialogChangedFiles.map((file) => (
                        <div key={file.path} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{file.path}</span>
                            <Badge variant="warning">changed</Badge>
                          </div>
                          <div className="mt-2 grid gap-2 text-xs md:grid-cols-2">
                            <div className="rounded bg-gray-100 p-2 dark:bg-gray-800">
                              <p className="mb-1 font-semibold text-gray-600 dark:text-gray-300">Current</p>
                              <pre className="max-h-28 overflow-auto whitespace-pre-wrap text-gray-600 dark:text-gray-300">{file.previousContent || '(new file)'}</pre>
                            </div>
                            <div className="rounded bg-gray-100 p-2 dark:bg-gray-800">
                              <p className="mb-1 font-semibold text-gray-600 dark:text-gray-300">Generated</p>
                              <pre className="max-h-28 overflow-auto whitespace-pre-wrap text-gray-600 dark:text-gray-300">{file.content || ''}</pre>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Roles selected</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{syncDialog?.roles.length || 0}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Streams</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{syncDialogBulkByStream.length}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Statuses</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">pending_sync and error roles only</p>
                  </div>
                </div>
                <div className="max-h-96 space-y-3 overflow-y-auto">
                  {syncDialogBulkByStream.map(([streamId, roles]) => (
                    <div key={streamId} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <h4 className="mb-2 font-semibold text-gray-900 dark:text-gray-100">{streamId}</h4>
                      <div className="space-y-2">
                        {roles.map((role) => (
                          <div key={getRoleKey(role)} className="flex items-start justify-between gap-3 rounded bg-gray-50 p-2 text-sm dark:bg-gray-800/60">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{role.agentTitle || role.code}</p>
                              <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{role.code}</p>
                              {role.syncState?.dirtyReasons?.length ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400">Reasons: {role.syncState.dirtyReasons.join(', ')}</p>
                              ) : null}
                              {role.syncState?.lastError ? (
                                <p className="text-xs text-red-500">Last error: {role.syncState.lastError}</p>
                              ) : null}
                            </div>
                            <Badge variant={role.syncState?.status === 'error' ? 'destructive' : 'warning'}>
                              {role.syncState?.status || 'pending_sync'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={closeSyncDialog} disabled={syncDialog?.syncing}>Cancel</Button>
            <Button
              onClick={() => void confirmSyncDialog()}
              disabled={!syncDialog || syncDialog.loading || syncDialog.syncing}
            >
              {syncDialog?.syncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : 'Proceed to sync'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trainerSyncDialog} onOpenChange={(open) => !open && closeTrainerSyncDialog()}>
        <DialogContent maxWidth="lg" className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Sync AI Training</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {trainerSyncDialog?.error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{trainerSyncDialog.error}</p>
            ) : null}
            {trainerSyncDialog?.loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Comparing trainer artifacts with GitHub...
              </div>
            ) : trainerSyncDialog?.repository ? (
              <>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Shared trainer repository</p>
                  <p className="break-all text-sm text-gray-900 dark:text-gray-100">{trainerSyncDialog?.repository || 'Not configured'}</p>
                </div>
                {trainerSyncDialog?.result ? (
                  <div className="rounded-md border border-gray-200 p-3 text-sm dark:border-gray-700">
                    {trainerSyncDialog.result.createdRepository ? (
                      <p className="text-gray-700 dark:text-gray-300">Created the trainer repository.</p>
                    ) : null}
                    {trainerSyncDialog.result.prUrl ? (
                      <p>
                        Opened pull request:{' '}
                        <a
                          href={trainerSyncDialog.result.prUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-violet-600 underline dark:text-violet-400"
                        >
                          {trainerSyncDialog.result.prUrl}
                        </a>
                      </p>
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300">Trainer artifacts already match the GitHub repo. No pull request was opened.</p>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {(trainerSyncDialog?.files || []).filter((file) => file.changed).length} changed files
                    </p>
                    {(trainerSyncDialog?.files || []).filter((file) => file.changed).length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No local trainer files differ from GitHub.</p>
                    ) : (
                      <div className="max-h-48 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
                        {(trainerSyncDialog?.files || []).filter((file) => file.changed).map((file) => (
                          <p key={file.path} className="font-mono text-xs px-3 py-1.5 text-gray-800 dark:text-gray-200">{file.path}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={closeTrainerSyncDialog} disabled={trainerSyncDialog?.loading || trainerSyncDialog?.syncing}>
              {trainerSyncDialog?.result ? 'Close' : 'Cancel'}
            </Button>
            {!trainerSyncDialog?.result ? (
              <Button
                onClick={() => void confirmTrainerSync()}
                disabled={
                  !trainerSyncDialog
                  || trainerSyncDialog.loading
                  || trainerSyncDialog.syncing
                  || Boolean(trainerSyncDialog.error)
                  || (trainerSyncDialog.files || []).filter((file) => file.changed).length === 0
                }
              >
                {trainerSyncDialog?.syncing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : 'Open sync PR'}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template editor */}
      <Dialog open={templateEditOpen} onOpenChange={(open) => !open && setTemplateEditOpen(false)}>
        <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Template: {templateEditStream}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-2 min-h-0">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Template defines default taxonomy, prompts, triage, review, and output targets for this stream.
            </p>
            {!templateYaml.trim() && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
                No template found for this stream. Templates define default taxonomy, prompts, triage, and review settings
                that new tenants inherit. You can create one here or leave it empty to use the values from the stream config directly.
              </div>
            )}
            <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
              <div className="max-h-[600px] overflow-auto custom-scrollbar">
                <Editor
                  value={templateYaml}
                  onValueChange={(v: string) => {
                    setTemplateYaml(v);
                    if (templateError) setTemplateError(null);
                  }}
                  highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                  padding={16}
                  style={{
                    fontFamily: '"Fira Code", "Fira Mono", monospace',
                    fontSize: 14,
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    minHeight: '400px',
                  }}
                  className="outline-none"
                />
              </div>
            </div>
            {templateError && <p className="text-sm text-red-600 dark:text-red-400">{templateError}</p>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTemplateEditOpen(false)} disabled={templateSaving}>Cancel</Button>
            <Button type="button" onClick={() => void handleTemplateSave()} disabled={templateSaving}>
              {templateSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Defaults editor */}
      <Dialog open={defaultsOpen} onOpenChange={(open) => !open && setDefaultsOpen(false)}>
        <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Defaults</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-2 min-h-0">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Global defaults: team types, ADO org, webhook URLs, and shared configuration.
              The <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">adoWebhookUrl</code> is used during onboarding to create ADO service hooks.
              The <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">botEndpointUrl</code> is the Teams Bot Framework endpoint.
              Jira and HubSpot MCP routes are available through portal MCP endpoints; email webhook endpoints remain configurable here.
            </p>
            <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
              <div className="max-h-[600px] overflow-auto custom-scrollbar">
                <Editor
                  value={defaultsYaml}
                  onValueChange={(v: string) => {
                    setDefaultsYaml(v);
                    if (defaultsError) setDefaultsError(null);
                  }}
                  highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                  padding={16}
                  style={{
                    fontFamily: '"Fira Code", "Fira Mono", monospace',
                    fontSize: 14,
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    minHeight: '400px',
                  }}
                  className="outline-none"
                />
              </div>
            </div>
            {defaultsError && <p className="text-sm text-red-600 dark:text-red-400">{defaultsError}</p>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDefaultsOpen(false)} disabled={defaultsSaving}>Cancel</Button>
            <Button type="button" onClick={() => void handleDefaultsSave()} disabled={defaultsSaving}>
              {defaultsSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OnboardStreamWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={() => void loadStreams()}
      />

      <ListenersDialog
        open={listenersOpen}
        streamId={listenersStreamId}
        streamDisplayName={listenersStreamName}
        roleCode={listenersRoleCode}
        roleLabel={listenersRoleLabel}
        onClose={() => {
          setListenersOpen(false);
          setListenersRoleCode(undefined);
          setListenersRoleLabel(undefined);
        }}
        onSaved={() => void loadStreams()}
      />

      <BusinessFlowDialog
        open={businessFlowOpen}
        streamId={defaultEntityDialogStreamId}
        streamDisplayName={
          streams.find((stream) => stream.streamId === defaultEntityDialogStreamId)?.displayName
          || defaultEntityDialogStreamId
        }
        onClose={() => setBusinessFlowOpen(false)}
      />

      <AiScheduleDialog
        open={schedulesOpen}
        streamId={schedulesStreamId}
        streamDisplayName={schedulesStreamName}
        roleCode={schedulesRoleCode}
        roleLabel={schedulesRoleLabel}
        onClose={() => {
          setSchedulesOpen(false);
          setSchedulesRoleCode(undefined);
          setSchedulesRoleLabel(undefined);
        }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteResources(null); } }}>
        <DialogContent maxWidth="lg" className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Delete: {deleteTarget?.tenantDisplayName}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({deleteTarget?.streamId} / {deleteTarget?.product} / {deleteTarget?.teamType})
              </span>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {/* Phase 1: Selection (before submit) */}
            {!deleteLoading && !deleteResult && deleteLiveSteps.length === 0 && (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This will remove the tenant <strong>{deleteTarget?.tenantCode}</strong> from the stream configuration.
                  If the team type or product becomes empty, they will be pruned too.
                  Select which associated resources should also be deleted:
                </p>
                {!deleteResources ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading resources…
                  </div>
                ) : deleteResources.length === 0 ? (
                  <p className="text-sm text-gray-500">No associated resources found. Only the config entry will be removed.</p>
                ) : (
                  <div className="space-y-2">
                    {deleteResources.map((r) => {
                      const key = getDeleteResourceKey(r);
                      const TYPE_LABELS: Record<string, string> = {
                        repo: 'Repository',
                        adoProject: 'ADO Project',
                        bot: 'Entra App Registration',
                        botService: 'Azure Bot Service',
                        teamsApp: 'Teams App',
                        teamsTeam: 'Teams Team',
                        teamsChat: 'Teams Group Chat',
                        teamsChannel: 'Teams Channel',
                        teamsTeamInstall: 'Team/Channel Bot Install',
                        teamsChatInstall: 'Group Chat Bot Install',
                        teamsUserInstall: 'Personal Bot Installs',
                      };
                      const typeLabel = TYPE_LABELS[r.type] || r.type;
                      return (
                        <label key={key} className={`flex items-center gap-2 text-sm ${r.shared ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            disabled={r.shared}
                            checked={deleteChecked.has(key)}
                            onChange={(e) => {
                              const next = new Set(deleteChecked);
                              if (e.target.checked) next.add(key); else next.delete(key);
                              setDeleteChecked(next);
                            }}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{typeLabel}:</span> {r.name}
                            {r.type === 'teamsChannel' && r.metadata?.channelKind ? (
                              <span className="text-gray-500"> ({r.metadata.channelKind})</span>
                            ) : null}
                          </span>
                          {r.shared && (
                            <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                              shared with other roles
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Phase 2: Streaming progress */}
            {(deleteLoading || deleteLiveSteps.length > 0) && !deleteResult && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Deleting resources…</p>
                <div className="text-xs space-y-1 max-h-80 overflow-y-auto">
                  {deleteLiveSteps.map((s: any, i: number) => {
                    const statusColor = s.status === 'completed' ? '#22c55e'
                      : s.status === 'failed' ? '#ef4444'
                      : s.status === 'running' ? '#3b82f6'
                      : '#9ca3af';
                    const textClass = s.status === 'failed' ? 'text-red-600'
                      : s.status === 'running' ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400';
                    return (
                      <div key={i} className={`flex items-start gap-2 ${textClass}`}>
                        <span className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ backgroundColor: statusColor }}>
                          {s.status === 'running' && (
                            <span className="block w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: statusColor }} />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span>{s.name}</span>
                          {s.detail && <span className="text-gray-400 ml-1">— {s.detail}</span>}
                          {s.error && <span className="text-red-500 ml-1 block">— {s.error}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {deleteLoading && deleteLiveSteps.length === 0 && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" /> Starting…
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Phase 3: Results */}
            {deleteResult && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant={deleteResult.overallStatus === 'completed' ? 'success' : deleteResult.overallStatus === 'partial' ? 'warning' : 'destructive'}>
                    {deleteResult.overallStatus}
                  </Badge>
                </div>
                <div className="text-xs space-y-1 max-h-80 overflow-y-auto">
                  {(deleteResult.steps || deleteLiveSteps).map((s: any, i: number) => {
                    const statusColor = s.status === 'completed' ? '#22c55e' : s.status === 'failed' ? '#ef4444' : '#9ca3af';
                    const textClass = s.status === 'failed' ? 'text-red-600' : 'text-gray-600 dark:text-gray-400';
                    return (
                      <div key={i} className={`flex items-start gap-2 ${textClass}`}>
                        <span className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ backgroundColor: statusColor }} />
                        <div className="flex-1 min-w-0">
                          <span>{s.name}</span>
                          {s.detail && <span className="text-gray-400 ml-1">— {s.detail}</span>}
                          {s.error && <span className="text-red-500 ml-1 block">— {s.error}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            {deleteResult ? (
              <Button variant="outline" onClick={() => {
                setDeleteTarget(null);
                setDeleteResources(null);
                setDeleteErrors([]);
                setDeleteLiveSteps([]);
                setDeleteResult(null);
              }}>Close</Button>
            ) : (deleteLoading || deleteLiveSteps.length > 0) ? null : (
              <>
                <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteResources(null); }}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => void confirmDelete()}
                  disabled={!deleteResources}
                >
                  Delete
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {retryingStream && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="max-w-sm w-full">
            <CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto mb-3" />
              <p className="text-sm text-gray-700 dark:text-gray-300">Retrying onboarding for <strong>{retryingStream}</strong>…</p>
            </CardContent>
          </Card>
        </div>
      )}

      <ManageStreamsDialog open={manageStreamsOpen} onClose={() => setManageStreamsOpen(false)} onChanged={() => void loadStreams()} streams={streams} />
      <ManageProductsDialog
        open={manageProductsOpen}
        onClose={() => setManageProductsOpen(false)}
        onChanged={() => void loadStreams()}
        streamId={managingStreamId || streamIds[0] || ''}
        streamOptions={streamPickerOptions}
        onStreamIdChange={setManagingStreamId}
      />
      <ManageTeamsDialog
        open={manageTeamsOpen}
        onClose={() => setManageTeamsOpen(false)}
        onChanged={() => void loadStreams()}
        streamId={managingStreamId || streamIds[0] || ''}
        streamOptions={streamPickerOptions}
        onStreamIdChange={setManagingStreamId}
      />
      <ManageTenantsDialog
        open={manageTenantsOpen}
        onClose={() => setManageTenantsOpen(false)}
        onChanged={() => void loadStreams()}
        streamId={managingStreamId || streamIds[0] || ''}
        streamOptions={streamPickerOptions}
        onStreamIdChange={setManagingStreamId}
      />

      <Dialog open={!!retryResult} onOpenChange={(open) => !open && setRetryResult(null)}>
        <DialogContent maxWidth="lg">
          <DialogHeader>
            <DialogTitle>Retry Onboarding Result</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {retryResult && (
              <div className="space-y-3">
                <p className="text-sm">
                  Stream: <strong>{retryResult.streamId}</strong> — Status:{' '}
                  <span className={retryResult.overallStatus === 'completed' ? 'text-green-600' : retryResult.overallStatus === 'partial' ? 'text-yellow-600' : 'text-red-600'}>
                    {retryResult.overallStatus}
                  </span>
                </p>
                <ul className="space-y-1 text-sm">
                  {retryResult.steps.map((step: any) => (
                    <li key={step.id} className="flex items-start gap-2">
                      <span className={step.status === 'completed' ? 'text-green-600' : step.status === 'skipped' ? 'text-gray-400' : step.status === 'failed' ? 'text-red-600' : 'text-gray-600'}>
                        {step.status === 'completed' ? '✓' : step.status === 'skipped' ? '—' : step.status === 'failed' ? '✗' : '·'}
                      </span>
                      <span>{step.name}</span>
                      {step.error && <span className="text-red-500 text-xs ml-1">({step.error})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetryResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
